import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { handleApiError } from './axiosConfig';
import { Camp } from '../utils/types';

export interface FavoritesResponse {
  camps: Camp[];
  uuid: string;
}

export interface SharedFavoritesResponse {
  camps: Camp[];
  uuid: string;
  shared_by: string;
}

export interface AddToFavoritesRequest {
  camp_id: string;
}

export interface ShareFavoritesResponse {
  share_uuid: string;
}



// GET /api/favorites/[favorite_id] - Get list of favorite camps
export const useGetFavorites = (favoriteId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryFn: async (): Promise<FavoritesResponse> => {
      try {
        return await apiClient.get<FavoritesResponse>(`/api/users/me/favorites/${favoriteId}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch favorites:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    queryKey: ['favorites', favoriteId],
    enabled: options?.enabled !== false && Boolean(favoriteId),
  });
};

export const useAddToFavorites = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      favoriteId, 
      campId 
    }: { 
      favoriteId: string; 
      campId: string; 
    }): Promise<FavoritesResponse> => {
      try {
        return await apiClient.put<FavoritesResponse>(`/api/users/me/favorites/${favoriteId}`, { 
          camp_id: campId 
        });
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to add camp to favorites:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data, { favoriteId }) => {
      // Invalidate and refetch the favorites list
      queryClient.invalidateQueries({
        queryKey: ['favorites', favoriteId],
      });
      
      // Optionally update the cache directly
      queryClient.setQueryData(
        ['favorites', favoriteId],
        data
      );
    },
    onError: (error) => {
      console.error('Failed to add camp to favorites:', error);
    },
  });
};

export const useRemoveFromFavorites = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      favoriteId, 
      campId 
    }: { 
      favoriteId: string; 
      campId: string; 
    }): Promise<FavoritesResponse> => {
      try {
        return await apiClient.delete<FavoritesResponse>(`/api/users/me/favorites/${favoriteId}/camps/${campId}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to remove camp from favorites:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data, { favoriteId }) => {
      // Invalidate and refetch the favorites list
      queryClient.invalidateQueries({
        queryKey: ['favorites', favoriteId],
      });
      
      // Optionally update the cache directly
      queryClient.setQueryData(
        ['favorites', favoriteId],
        data
      );
    },
    onError: (error) => {
      console.error('Failed to remove camp from favorites:', error);
    },
  });
};

export const useShareFavorites = () => {
  return useMutation({
    mutationFn: async (favoriteUuid: string): Promise<ShareFavoritesResponse> => {
      try {
        return await apiClient.post<ShareFavoritesResponse>(`/api/users/me/favorites/${favoriteUuid}/share`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to generate share link:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onError: (error) => {
      console.error('Failed to generate share link:', error);
    },
  });
};

export const useGetSharedFavorites = (favoriteUuid: string, shareUuid: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryFn: async (): Promise<SharedFavoritesResponse> => {
      try {
        return await apiClient.get<SharedFavoritesResponse>(`/api/favorites/${favoriteUuid}/share/${shareUuid}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch shared favorites:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    queryKey: ['shared-favorites', favoriteUuid, shareUuid],
    enabled: options?.enabled !== false && Boolean(favoriteUuid) && Boolean(shareUuid),
  });
};
